/**
 * OaSIS Explorer Frontend - Auth Context
 * Firebase Authentication state management
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile as firebaseUpdateProfile,
} from 'firebase/auth';
import { firebaseAuth } from '../config/firebase';

// Auth state type
interface AuthState {
  user: FirebaseUser | null;
  loading: boolean;
  error: string | null;
}

// Auth context type
interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateDisplayName: (displayName: string) => Promise<void>;
  getIdToken: () => Promise<string | null>;
  clearError: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Context Provider
 * Manages Firebase authentication state
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true, // Start loading until we check auth state
    error: null,
  });

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      setState((prev) => ({
        ...prev,
        user,
        loading: false,
      }));
    });

    return () => unsubscribe();
  }, []);

  /**
   * Sign in with email and password
   */
  const signIn = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await signInWithEmailAndPassword(firebaseAuth, email, password);
    } catch (error: unknown) {
      const firebaseError = error as { code?: string };
      setState((prev) => ({
        ...prev,
        loading: false,
        error: getErrorMessage(firebaseError.code || 'unknown'),
      }));
      throw error;
    }
  }, []);

  /**
   * Sign up with email and password
   */
  const signUp = useCallback(async (email: string, password: string, displayName?: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { user } = await createUserWithEmailAndPassword(firebaseAuth, email, password);
      
      // Update display name if provided
      if (displayName && user) {
        await firebaseUpdateProfile(user, { displayName });
      }
    } catch (error: unknown) {
      const firebaseError = error as { code?: string };
      setState((prev) => ({
        ...prev,
        loading: false,
        error: getErrorMessage(firebaseError.code || 'unknown'),
      }));
      throw error;
    }
  }, []);

  /**
   * Sign out
   */
  const signOut = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await firebaseSignOut(firebaseAuth);
    } catch (error: unknown) {
      const firebaseError = error as { code?: string };
      setState((prev) => ({
        ...prev,
        loading: false,
        error: getErrorMessage(firebaseError.code || 'unknown'),
      }));
      throw error;
    }
  }, []);

  /**
   * Send password reset email
   */
  const resetPassword = useCallback(async (email: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await sendPasswordResetEmail(firebaseAuth, email);
      setState((prev) => ({ ...prev, loading: false }));
    } catch (error: unknown) {
      const firebaseError = error as { code?: string };
      setState((prev) => ({
        ...prev,
        loading: false,
        error: getErrorMessage(firebaseError.code || 'unknown'),
      }));
      throw error;
    }
  }, []);

  /**
   * Update display name
   */
  const updateDisplayName = useCallback(async (displayName: string) => {
    if (!state.user) throw new Error('No user signed in');
    
    await firebaseUpdateProfile(state.user, { displayName });
    // Force refresh to get updated user
    setState((prev) => ({ ...prev, user: firebaseAuth.currentUser }));
  }, [state.user]);

  /**
   * Get current ID token for API requests
   */
  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (!state.user) return null;
    return state.user.getIdToken();
  }, [state.user]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const contextValue: AuthContextType = {
    ...state,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateDisplayName,
    getIdToken,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

/**
 * Convert Firebase error codes to user-friendly messages
 */
function getErrorMessage(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled.';
    case 'auth/weak-password':
      return 'Password is too weak. Use at least 6 characters.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/wrong-password':
      return 'Incorrect password.';
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    default:
      return 'An error occurred. Please try again.';
  }
}

